import type { AgentTokenPublic } from 'types';
import type { AgentTokenExpiryDays, AgentTokenScope } from 'definitions/agent-tokens';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { WebAuthnError } from '@simplewebauthn/browser';
import { useAuth } from 'hooks/useAuth';
import { can } from 'definitions/capabilities';
import {
  DEFAULT_AGENT_TOKEN_EXPIRY_DAYS,
  MAX_AGENT_TOKEN_LIMIT_IDS,
  MAX_AGENT_TOKEN_NAME_LENGTH,
  STAFF_TOKEN_REFUSAL_MESSAGES,
  isStaffWriteToken,
  staffTokenRefusal,
} from 'definitions/agent-tokens';
import { useAgentTokens } from '@/state/useAgentTokens';
import { apiErrorMessage } from '@/state/api-error';
import { AGENT_ACCESS_COPY } from './agent-access-copy';

export type WebsiteMode = 'all' | 'some';

type TokenStatus = 'active' | 'expired' | 'revoked';

type StatusParams = { token: Pick<AgentTokenPublic, 'revokedAt' | 'expiresAt'>; now: number };

export const tokenStatus = ({ token, now }: StatusParams): TokenStatus =>
  token.revokedAt !== null ? 'revoked' : token.expiresAt <= now ? 'expired' : 'active';

type BlockingParams = {
  name: string;
  scopes: readonly AgentTokenScope[];
  websiteMode: WebsiteMode;
  websiteIds: readonly number[];
  expiresInDays: number;
  password: string;
  /** The person making it, for the staff rule (staffTokenRefusal), which the server applies too. */
  user: { role?: string | null; companyId?: number | null } | null | undefined;
  /** The account's passkey cannot answer on this site (passkeys are bound to their host). */
  passkeyElsewhere?: boolean;
};

/**
 * What to say when a create fails. @simplewebauthn/browser wraps the browser's DOMException in a
 * WebAuthnError that keeps its name (as signin-failure-reason.ts relies on), and neither's text is
 * written for a person, so a passkey failure gets our own line. The API's refusals are shown as sent.
 */
export const createFailureMessage = (error: unknown): string => {
  if (error instanceof WebAuthnError || error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'AbortError'
      ? AGENT_ACCESS_COPY.passkeyCancelled
      : AGENT_ACCESS_COPY.passkeyFailed;
  }
  return apiErrorMessage(error, AGENT_ACCESS_COPY.createFailed);
};

/**
 * Where the account's passkeys are, as a person would name them: the host for a web origin, the
 * Chrome extension for an extension origin.
 */
export const passkeyPlaces = (origins: readonly string[]): string[] => [
  ...new Set(
    origins.map((origin) => {
      if (origin.startsWith('chrome-extension://')) return AGENT_ACCESS_COPY.extensionPlace;
      try {
        return new URL(origin).host;
      } catch {
        return origin;
      }
    }),
  ),
];

/** Why Create is dead, in one sentence, or undefined when it is not. */
export const createBlockingReason = ({
  name,
  scopes,
  websiteMode,
  websiteIds,
  expiresInDays,
  password,
  user,
  passkeyElsewhere = false,
}: BlockingParams): string | undefined => {
  if (passkeyElsewhere) return AGENT_ACCESS_COPY.passkeyElsewhereShort;
  if (!name.trim() || name.trim().length > MAX_AGENT_TOKEN_NAME_LENGTH) return AGENT_ACCESS_COPY.needName;
  if (scopes.length === 0) return AGENT_ACCESS_COPY.needScope;
  if (websiteMode === 'some' && websiteIds.length === 0) return AGENT_ACCESS_COPY.noWebsitesYet;
  if (websiteMode === 'some' && websiteIds.length > MAX_AGENT_TOKEN_LIMIT_IDS) {
    return AGENT_ACCESS_COPY.tooManyWebsites(MAX_AGENT_TOKEN_LIMIT_IDS);
  }
  const staffRefusal = staffTokenRefusal({
    user,
    scopes,
    websiteIds: websiteMode === 'some' ? websiteIds : null,
    companyIds: null,
    expiresInDays,
  });
  if (staffRefusal) return STAFF_TOKEN_REFUSAL_MESSAGES[staffRefusal];
  if (!password) return AGENT_ACCESS_COPY.needPassword;
  return undefined;
};

type McpCommandParams = { origin: string; secret: string };

/**
 * The command that adds Pertento to Claude Code with this token, for the page's own origin: the
 * Dashboard process serves both the page and /mcp, so that is https://app.pertento.ai/mcp in
 * production, the URL connectors and docs/server/mcp.md use, and the staging host on staging.
 */
export const mcpAddCommand = ({ origin, secret }: McpCommandParams): string =>
  `claude mcp add --transport http pertento ${origin}/mcp --header "Authorization: Bearer ${secret}"`;

/**
 * Everything the Agent access screen holds: the create dialog and its form, the secret from the
 * last create (which exists only in this state and is gone once the dialog closes or the page is
 * left), and which revoke confirm is showing. Owners and Admins (`team:manage`) also get their team's tokens.
 */
export const useAgentAccess = () => {
  const { user } = useAuth();
  const canManageTeam = can(user, 'team:manage');
  const data = useAgentTokens(canManageTeam);
  const { options } = data;

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<AgentTokenScope[]>(['read']);
  // Some websites by default: a token reaching everything should be a choice, not the starting point.
  const [websiteMode, setWebsiteMode] = useState<WebsiteMode>('some');
  const [websiteIds, setWebsiteIds] = useState<number[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<AgentTokenExpiryDays>(DEFAULT_AGENT_TOKEN_EXPIRY_DAYS);
  const [password, setPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // The last create's failure, shown in the form: a passkey or password refusal must be read there.
  const [createError, setCreateError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [commandCopied, setCommandCopied] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [revoking, setRevoking] = useState<AgentTokenPublic | null>(null);
  // Counts each create, so a create that answers after a newer one cannot show a stale secret.
  const attempt = useRef(0);

  // One list from the server: personal access tokens, and connected apps (OAuth, PER-334).
  const personalTokens = data.tokens.filter((token) => token.kind !== 'oauth');
  const connectedApps = data.tokens.filter((token) => token.kind === 'oauth');
  const allowedScopes = options?.scopes ?? [];
  const readOnly = allowedScopes.length > 0 && allowedScopes.every((scope) => scope === 'read');
  // A staff token that can make changes must name its sites: the form offers no "every website".
  const staffWrite = isStaffWriteToken({ user, scopes });
  // Passkeys are bound to their host, so one registered elsewhere cannot confirm a create here.
  const passkeyElsewhere = !!options?.passkeyStepUp && !options.passkeyUsableHere;
  const passkeyNotice = passkeyElsewhere
    ? AGENT_ACCESS_COPY.passkeyElsewhere(passkeyPlaces(options?.passkeyOrigins ?? []))
    : null;
  // With no options there is nothing to choose from, and the form's own reasons would mislead.
  const blockingReason = data.isOptionsError
    ? AGENT_ACCESS_COPY.optionsFailed
    : createBlockingReason({
        name,
        scopes,
        websiteMode,
        websiteIds,
        expiresInDays,
        password,
        user,
        passkeyElsewhere,
      });
  const now = Date.now();

  /** Ticking a write scope on a staff account moves the websites choice to the ones they pick. */
  const toggleScope = (scope: AgentTokenScope) => {
    const next = scopes.includes(scope) ? scopes.filter((item) => item !== scope) : [...scopes, scope];
    setScopes(next);
    if (isStaffWriteToken({ user, scopes: next })) setWebsiteMode('some');
  };

  const toggleWebsite = (websiteId: number) =>
    setWebsiteIds((current) =>
      current.includes(websiteId) ? current.filter((item) => item !== websiteId) : [...current, websiteId],
    );

  /** Every website in the list, as explicit ids; with all of them ticked already, none. */
  const toggleAllWebsites = () => {
    const all = options?.websites.map((website) => website.id) ?? [];
    setWebsiteIds((current) => (current.length >= all.length ? [] : all));
  };

  const resetForm = () => {
    setName('');
    setScopes(['read']);
    setWebsiteMode('some');
    setWebsiteIds([]);
    setExpiresInDays(DEFAULT_AGENT_TOKEN_EXPIRY_DAYS);
    setPassword('');
  };

  const create = async () => {
    if (blockingReason || isCreating) return;
    attempt.current += 1;
    const startedAt = attempt.current;
    setIsCreating(true);
    setCreateError(null);
    try {
      // An account with a passkey (on any origin) confirms with one too; the server checks it.
      const passkey = options?.passkeyStepUp ? await data.confirmWithPasskey() : undefined;
      const created = await data.create({
        name: name.trim(),
        scopes,
        websiteIds: websiteMode === 'some' ? websiteIds : null,
        expiresInDays,
        password,
        ...(passkey ? { passkey } : {}),
      });
      if (attempt.current !== startedAt) return;
      setSecret(created);
      setCopied(false);
      resetForm();
    } catch (error) {
      if (attempt.current !== startedAt) return;
      // In the form, next to Create: a 403 from the passkey step-up or a wrong password is the answer.
      setCreateError(createFailureMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      toast.error(AGENT_ACCESS_COPY.copyFailed);
    }
  };

  const mcpCommand = secret ? mcpAddCommand({ origin: window.location.origin, secret }) : null;

  const copyCommand = async () => {
    if (!mcpCommand) return;
    try {
      await navigator.clipboard.writeText(mcpCommand);
      setCommandCopied(true);
    } catch {
      toast.error(AGENT_ACCESS_COPY.copyFailed);
    }
  };

  const openCreate = () => {
    setCreateError(null);
    setIsDialogOpen(true);
  };

  /** Closing forgets the secret and the password, whatever else was typed stays for next time. */
  const closeDialog = () => {
    setIsDialogOpen(false);
    setConfirmingClose(false);
    setSecret(null);
    setCopied(false);
    setCommandCopied(false);
    setPassword('');
    setCreateError(null);
  };

  /**
   * Every way out of the dialog comes here. Not while a create is running, or the secret it answers
   * with would have nowhere to go. Not uncopied either: the command carries the token too, so
   * copying either will do, and otherwise it asks, because the token cannot be shown again.
   */
  const requestClose = () => {
    if (isCreating) return;
    if (secret && !copied && !commandCopied) {
      setConfirmingClose(true);
      return;
    }
    closeDialog();
  };

  const cancelClose = () => setConfirmingClose(false);

  const askRevoke = (token: AgentTokenPublic) => setRevoking(token);
  const cancelRevoke = () => setRevoking(null);

  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      await data.revoke(revoking.id);
      const done = revoking.kind === 'oauth' ? AGENT_ACCESS_COPY.disconnectedToast : AGENT_ACCESS_COPY.revokedToast;
      toast.success(done(revoking.name));
    } catch (error) {
      // The lists have been refreshed by now, so they show whether it is in fact revoked.
      toast.error(apiErrorMessage(error, AGENT_ACCESS_COPY.revokeFailed));
    } finally {
      setRevoking(null);
    }
  };

  return {
    ...data,
    tokens: personalTokens,
    connectedApps,
    canManageTeam,
    now,
    allowedScopes,
    readOnly,
    name,
    setName,
    scopes,
    toggleScope,
    websiteMode,
    setWebsiteMode,
    websiteIds,
    toggleWebsite,
    toggleAllWebsites,
    expiresInDays,
    setExpiresInDays,
    password,
    setPassword,
    blockingReason,
    staffWrite,
    passkeyNotice,
    createError,
    isCreating,
    create,
    secret,
    copied,
    copySecret,
    mcpCommand,
    commandCopied,
    copyCommand,
    isDialogOpen,
    openCreate,
    requestClose,
    confirmingClose,
    cancelClose,
    confirmClose: closeDialog,
    revoking,
    askRevoke,
    cancelRevoke,
    confirmRevoke,
  };
};

export type AgentAccessManager = ReturnType<typeof useAgentAccess>;
